import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { isMobileShellRoute, findMobileTitle } from "../../lib/mobileRoutes";
import MobileAppDrawer from "./MobileAppDrawer";

/**
 * Global mobile top bar host — mounted once at the app root.
 *
 * Renders:
 *   • A fixed 56px gradient top bar with a hamburger (left) + centered page
 *     title (white) — on every mobile shell route EXCEPT the Home surface
 *     (/dashboard, /mobile), which renders its own full hero header instead.
 *   • The MobileAppDrawer slide-in menu (always mounted on shell routes, so
 *     it can be opened either from this bar's hamburger, from MobileHome's
 *     own hamburger, or from the bottom nav's "More" button — all three
 *     just dispatch the shared `open-mobile-drawer` window event).
 *
 * Visibility is gated on:
 *   - authenticated user (every role, including technicians — the shell is
 *     identical for everyone)
 *   - a "shell" route (excludes /mobile/jobs/*, /mobile/guru, /mobile/calendar,
 *     /platform/*, and public marketing/auth/payment/form routes — see
 *     mobileRoutes.js)
 *   - viewport < md via the `md:hidden` wrapper
 */
export default function MobileTopBarHost() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener("open-mobile-drawer", openIt);
    return () => window.removeEventListener("open-mobile-drawer", openIt);
  }, []);

  if (!user) return null;
  if (!isMobileShellRoute(location.pathname)) return null;

  const isHomeRoute = location.pathname === "/dashboard" || location.pathname === "/mobile";
  const hasCustomHeader = location.pathname === "/dispatch"; // renders its own hamburger+logo+search header
  const title = findMobileTitle(location.pathname) || (user.company?.name || "");

  return (
    <>
      {!isHomeRoute && !hasCustomHeader && (
        <header
          className="mv md:hidden fixed top-0 inset-x-0 z-40 h-14 flex items-center px-2 shadow-sm"
          data-testid="mobile-top-bar"
          style={{ paddingTop: "env(safe-area-inset-top)", background: "linear-gradient(to right, #0077CC, #409EE6)" }}
        >
          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-top-bar-menu"
            aria-label="Open menu"
            className="h-10 w-10 rounded-full flex items-center justify-center active:bg-white/15"
          >
            <Menu className="h-6 w-6" color="#ffffff" />
          </button>
          <div className="flex-1 text-center pr-10 text-[16px] font-bold tracking-tight text-white" data-testid="mobile-top-bar-title">
            {title}
          </div>
        </header>
      )}

      <MobileAppDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}
